import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AtSign, Save, RotateCcw } from "lucide-react";

/**
 * Manager-only card that lets admins change the outbound Resend "from"
 * address in-app (no redeploy). Backend persists the override in
 * app_settings; leaving the field empty deletes the override and reverts
 * to the SENDER_EMAIL env var. Bounces landing in a mailbox that doesn't
 * exist on the verified domain motivated this — admins can now redirect
 * bounces to a real inbox without emailing support.
 */
const SenderEmailCard = () => {
  const [s, setS] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/sender-email");
      setS(data);
      setValue(data.override || "");
    } catch {
      // Non-managers get 403 — silently skip so we don't spam toasts.
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!s) return null;

  const save = async () => {
    const trimmed = value.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return toast.error("Enter a valid email address");
    }
    setBusy(true);
    try {
      const { data } = await api.put("/settings/sender-email", { value: trimmed });
      setS(data);
      setValue(data.override || "");
      toast.success(
        trimmed ? "Sender email saved" : "Reverted to env default"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setValue("");
    setBusy(true);
    try {
      const { data } = await api.put("/settings/sender-email", { value: "" });
      setS(data);
      toast.success("Reverted to env default");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nb-card p-5" data-testid="sender-email-card">
      <div className="flex items-center gap-2 mb-2">
        <AtSign size={20} strokeWidth={2.5} className="text-[#228EBF]" />
        <h2 className="font-display text-xl">Outbound sender address</h2>
      </div>
      <div className="text-xs text-[#607087] mb-3">
        The &ldquo;From&rdquo; address on every PO, digest and renewal email.{" "}
        <b>Must be on a domain verified in Resend.</b> Bounces are delivered
        here, so pick a real inbox someone reads (e.g.{" "}
        <span className="font-mono">warehouse@footwork.com.au</span>).
      </div>

      <div className="grid grid-cols-1 gap-3">
        <label>
          <div className="label-eyebrow mb-1">Override (leave empty for env default)</div>
          <input
            data-testid="sender-email-input"
            type="email"
            className="nb-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={s.env_default || "warehouse@footwork.com.au"}
          />
        </label>

        <div
          className="border-2 border-black rounded-xl p-3 bg-[#F4F6FA] text-sm"
          data-testid="sender-email-current"
        >
          <div className="label-eyebrow">Currently sending as</div>
          <div className="font-mono-num text-base mt-1">{s.value}</div>
          <div className="text-xs text-[#607087] mt-1">
            Source: {s.source === "db" ? "in-app override" : "SENDER_EMAIL env var"}
          </div>
          {s.source === "db" && s.env_default && s.env_default !== s.value && (
            <div className="mt-2 pt-2 border-t border-[#607087]/20 text-xs text-[#607087]">
              <b>Note:</b> the deploy Secrets panel still shows the env value{" "}
              <span className="font-mono">{s.env_default}</span> &mdash; that&apos;s
              normal. This in-app override wins at send-time, so no redeploy is needed.
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            data-testid="sender-email-save"
            onClick={save}
            disabled={busy}
            className="nb-btn nb-btn-primary px-4 py-2.5 flex items-center gap-2"
          >
            <Save size={16} strokeWidth={2.5} /> Save
          </button>
          {s.source === "db" && (
            <button
              data-testid="sender-email-revert"
              onClick={revert}
              disabled={busy}
              className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2"
            >
              <RotateCcw size={16} strokeWidth={2.5} /> Revert to env default
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SenderEmailCard;
